import moment from 'moment/moment'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styles from './ListView.module.scss'
import Type from '@/component/Primitive/Type'
import DateLabel from '@/component/Primitive/Events/components/EventAgendaNavigator/components/Common/DateLabel'
import CheckboxList from '@/component/Primitive/Events/components/EventAgendaNavigator/components/Common/CheckboxList'
import ListViewTable from '@/component/Primitive/Events/components/EventAgendaNavigator/components/List/ListViewTable'
import cx from 'classnames'
import { object, string } from 'prop-types'

const ListView = ({
  eventUmbrella,
  baseURL,
  selectedDay,
  currentDateItem,
  timezoneAbbreviation,
  selectedLocation,
  ViewFilters
}) => {
  const [showFilters, setShowFilters] = useState(false)
  const [filtersHeight, setFiltersHeight] = useState(0)
  const filtersElement = useRef(null)
  const list = eventUmbrella?.event?.agenda || []

  const { dayMap, inverseDayMap } = useMemo(() => {
    const dayMap = {}
    const inverseDayMap = {}

    let dayCount = 1
    let lastAgendaDate = null

    list.forEach((item) => {
      if (!lastAgendaDate) {
        dayMap[dayCount] = [{ ...item }]
      } else {
        const isSameDay = moment(item.startDate).isSame(lastAgendaDate, 'day')
        if (!isSameDay) {
          dayCount++
          dayMap[dayCount] = []
        }
        dayMap[dayCount].push({ ...item })
      }

      inverseDayMap[item._id] = dayCount
      lastAgendaDate = item.startDate
    })

    return { dayMap, inverseDayMap }
  }, [list])

  // Memoized companies extraction
  const allCompanies = useMemo(() => {
    const companyNames = new Set()

    list.forEach((agenda) =>
      agenda?.speakers?.forEach((speaker) => {
        if (speaker?.companyName) {
          companyNames.add(speaker.companyName)
        }
      })
    )

    return Array.from(companyNames).sort()
  }, [list])

  // Memoized event types extraction
  const allEventTypes = useMemo(() => {
    const sessionTypes = new Set()

    list.forEach((agenda) => {
      if (agenda.sessionType?.shortName) {
        if (agenda.sessionType.shortName.toLowerCase().includes('network')) {
          return
        }

        sessionTypes.add(agenda.sessionType.shortName)
      }
    })

    return Array.from(sessionTypes).sort()
  }, [list])

  // State for filters
  const [filteredCompanies, setFilteredCompanies] = useState([])
  const [filteredEventTypes, setFilteredEventTypes] = useState([])

  // Memoized filter handler to prevent unnecessary re-renders
  const handleFilterStateChange = useCallback((event, setter) => {
    const { checked, value } = event.target

    setter((prevTypes) =>
      checked
        ? [...prevTypes, value]
        : prevTypes.filter((type) => type !== value)
    )
  }, [])

  // Early return if no agenda items
  if (!list.length) {
    return null
  }

  // Determine if stage label should be hidden
  const hideStageLabel = Object.keys(dayMap).length === 1

  const handleFiltersToggle = (_event) => {
    setShowFilters((value) => !value)
    setFiltersHeight((prev) =>
      prev > 0 ? 0 : filtersElement.current.scrollHeight
    )
  }

  return (
    <div className={styles.ListViewGrid}>
      <div>
        <Type
          as="h3"
          size={['heading5', 'heading4']}
          weight="bold"
          className={cx(
            styles.ListViewColumnHeading,
            styles.ListViewColumnHeadingFilters
          )}
        >
          Filters
        </Type>

        <button
          type="button"
          onClick={handleFiltersToggle}
          className={cx(
            styles.ListViewButtonToggle,
            showFilters ? styles.ListViewButtonToggleActive : null,
            styles.ListViewColumnHeading
          )}
        >
          <Type as="span" size={['heading5', 'heading4']} weight="bold">
            Filters
          </Type>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path d="M0 7.33l2.829-2.83 9.175 9.339 9.167-9.339 2.829 2.83-11.996 12.17z" />
          </svg>
        </button>

        <ViewFilters />

        <div
          ref={filtersElement}
          style={{ height: `${filtersHeight}px` }}
          className={styles.ListViewFieldsSets}
        >
          <fieldset className={styles.ListViewFieldSet}>
            <legend>Session types</legend>
            <CheckboxList
              name="types"
              items={allEventTypes}
              values={filteredEventTypes}
              onChange={(event) =>
                handleFilterStateChange(event, setFilteredEventTypes)
              }
            />
          </fieldset>

          <fieldset className={styles.ListViewFieldSet}>
            <legend>Companies</legend>
            <CheckboxList
              name="speakers"
              items={allCompanies}
              values={filteredCompanies}
              onChange={(event) =>
                handleFilterStateChange(event, setFilteredCompanies)
              }
            />
          </fieldset>
        </div>
      </div>

      <div>
        <div className={styles.ListViewColumnHeading}>
          <DateLabel date={currentDateItem?.startDate} day={selectedDay} />
        </div>

        <ListViewTable
          list={list}
          dayMap={dayMap}
          inverseDayMap={inverseDayMap}
          baseURL={baseURL}
          hideStageLabel={hideStageLabel}
          currentDateItem={currentDateItem}
          filteredCompanies={filteredCompanies}
          filteredEventTypes={filteredEventTypes}
          selectedLocation={selectedLocation}
          timezoneAbbreviation={timezoneAbbreviation}
        />
      </div>
    </div>
  )
}

ListView.propTypes = {
  eventUmbrella: object,
  baseURL: string,
  selectedDay: string,
  currentDateItem: object,
  timezoneAbbreviation: string,
  selectedLocation: string,
  ViewFilters: React.Component
}

export default ListView
